/**
 * The three ways a panel can be shown on a phone.
 *
 * All three walk the panel in the exact planogram order (shelf 1 to 11, facing 1
 * to 6) so a rep can always map what they see here onto the physical rack:
 *
 *  - `RackView`  - a true 6x11 miniature of the panel. Best for locating a
 *                  product by where it physically sits. Names are tiny, so it
 *                  leans on form colours and a zoom control.
 *  - `ShelfView` - one horizontally snapping strip per shelf. Keeps the real 2D
 *                  placement while making names and prices readable.
 *  - `ListView`  - full-width rows grouped by shelf. Largest touch targets and
 *                  no horizontal scrolling, for ordering at speed.
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { Minus, Plus, AlertTriangle, HelpCircle } from 'lucide-react';
import { PlanogramPanel, PlanogramProduct, PlanogramSlot } from '../../@types/planogram';
import {
  BorderRadius,
  Shadows,
  Spacing,
  ThemeColors,
  Typography,
} from '../../styles/ThemeStyles';
import {
  FORM_COLORS,
  PlanogramFilter,
  formatPrice,
  matchesQuery,
  passesFilter,
  positionBadge,
  slotState,
  tapFeedback,
} from './planogramTheme';

export type RackZoom = 'fit' | 'md' | 'lg';

const RACK_METRICS: Record<RackZoom, { track: string; height: number; font: number }> = {
  fit: { track: 'minmax(0, 1fr)', height: 64, font: 7.5 },
  md: { track: '104px', height: 86, font: 10 },
  lg: { track: '148px', height: 108, font: 12 },
};

interface ViewProps {
  panel: PlanogramPanel;
  /** Ordered quantity for a product, keyed by `PlanogramProduct.key`. */
  quantities: Record<string, number>;
  onSelectSlot: (slot: PlanogramSlot) => void;
  onChangeQuantity: (product: PlanogramProduct, qty: number) => void;
  query: string;
  filter: PlanogramFilter;
}

/* ── Shared pieces ─────────────────────────────────────────────────────────── */

const QtyBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  border-radius: 8px;
  background: ${ThemeColors.primary.dark};
  color: #fff;
  font-size: 9px;
  font-weight: ${Typography.fontWeight.bold};
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 1.5px #fff;
`;

const EmptyState = styled.p`
  margin: ${Spacing.xl} auto;
  max-width: 280px;
  text-align: center;
  color: ${ThemeColors.text.hint};
  font-size: ${Typography.fontSize.sm};
`;

const StepperWrap = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.background.paper};
  overflow: hidden;
  flex-shrink: 0;
`;

const StepperButton = styled.button<{ $size: number }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  display: grid;
  place-items: center;
  border: none;
  background: transparent;
  color: ${ThemeColors.primary.dark};
  cursor: pointer;
  touch-action: manipulation;

  &:active {
    background: ${ThemeColors.background.default};
  }

  &:disabled {
    color: ${ThemeColors.gray[400]};
    cursor: not-allowed;
  }
`;

const StepperValue = styled.span<{ $size: number; $active: boolean }>`
  min-width: ${(p) => p.$size - 6}px;
  text-align: center;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$active ? ThemeColors.primary.dark : ThemeColors.text.disabled)};
  border-left: 1px solid ${ThemeColors.border.light};
  border-right: 1px solid ${ThemeColors.border.light};
  align-self: stretch;
  line-height: ${(p) => p.$size}px;
`;

interface QtyStepperProps {
  qty: number;
  onChange: (qty: number) => void;
  size?: number;
  label: string;
}

export const QtyStepper: React.FC<QtyStepperProps> = ({ qty, onChange, size = 40, label }) => {
  const step = (delta: number) => (event: React.MouseEvent) => {
    event.stopPropagation();
    tapFeedback();
    onChange(Math.max(0, qty + delta));
  };

  return (
    <StepperWrap onClick={(e) => e.stopPropagation()}>
      <StepperButton
        $size={size}
        onClick={step(-1)}
        disabled={qty === 0}
        aria-label={`Decrease quantity for ${label}`}
      >
        <Minus size={size < 36 ? 13 : 16} aria-hidden="true" />
      </StepperButton>
      <StepperValue $size={size} $active={qty > 0} aria-live="polite">
        {qty}
      </StepperValue>
      <StepperButton $size={size} onClick={step(1)} aria-label={`Increase quantity for ${label}`}>
        <Plus size={size < 36 ? 13 : 16} aria-hidden="true" />
      </StepperButton>
    </StepperWrap>
  );
};

/** Small stock/SKU marker shared by the readable views. */
const StatusChip = styled.span<{ $tone: 'oos' | 'missing' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 5px;
  border-radius: ${BorderRadius.sm};
  font-size: 10px;
  font-weight: ${Typography.fontWeight.semiBold};
  ${(p) =>
    p.$tone === 'oos'
      ? css`
          background: rgba(244, 67, 54, 0.12);
          color: ${ThemeColors.error.dark};
        `
      : css`
          background: ${ThemeColors.gray[200]};
          color: ${ThemeColors.gray[700]};
        `}
`;

const ProductStatus: React.FC<{ product: PlanogramProduct }> = ({ product }) => {
  if (product.missing) {
    return (
      <StatusChip $tone="missing">
        <HelpCircle size={10} aria-hidden="true" />
        No portal SKU
      </StatusChip>
    );
  }
  if (product.outOfStock) {
    return (
      <StatusChip $tone="oos">
        <AlertTriangle size={10} aria-hidden="true" />
        Out of stock
      </StatusChip>
    );
  }
  return null;
};

/** Shared visibility rule: dim in the rack map, hide in the readable views. */
function useSlotVisibility(
  { quantities, query, filter }: Pick<ViewProps, 'quantities' | 'query' | 'filter'>
) {
  return React.useCallback(
    (product: PlanogramProduct) =>
      matchesQuery(product, query) && passesFilter(product, filter, quantities[product.key] ?? 0),
    [quantities, query, filter]
  );
}

/* ── Rack view ─────────────────────────────────────────────────────────────── */

const RackScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: ${Spacing.xs};
`;

const RackGrid = styled.div<{ $cols: number; $track: string; $fit: boolean }>`
  display: grid;
  grid-template-columns: 24px repeat(${(p) => p.$cols}, ${(p) => p.$track});
  gap: 2px;
  padding: 3px;
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.primary.dark};
  width: ${(p) => (p.$fit ? '100%' : 'max-content')};
`;

const Gutter = styled.div`
  position: sticky;
  left: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  background: ${ThemeColors.primary.dark};
  color: rgba(255, 255, 255, 0.85);
  font-size: 9px;
  font-weight: ${Typography.fontWeight.bold};
  font-variant-numeric: tabular-nums;
`;

const ColHeader = styled.div`
  display: grid;
  place-items: center;
  height: 16px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 9px;
  font-weight: ${Typography.fontWeight.bold};
`;

const RackCell = styled.button<{
  $height: number;
  $font: number;
  $accent: string;
  $dimmed: boolean;
  $oos: boolean;
  $missing: boolean;
  $ordered: boolean;
}>`
  position: relative;
  height: ${(p) => p.$height}px;
  padding: 4px 3px 3px;
  border: none;
  border-top: 3px solid ${(p) => p.$accent};
  border-radius: 3px;
  background: ${(p) => (p.$ordered ? '#E8F4E2' : ThemeColors.background.paper)};
  color: ${ThemeColors.text.primary};
  font-family: ${Typography.fontFamily.primary};
  font-size: ${(p) => p.$font}px;
  line-height: 1.15;
  font-weight: ${Typography.fontWeight.semiBold};
  text-align: left;
  overflow: hidden;
  cursor: pointer;
  touch-action: manipulation;
  opacity: ${(p) => (p.$dimmed ? 0.22 : 1)};
  transition: opacity 120ms ease, box-shadow 120ms ease;
  box-shadow: ${(p) => (p.$ordered ? `inset 0 0 0 2px ${ThemeColors.primary.main}` : 'none')};

  ${(p) =>
    p.$oos &&
    css`
      background-image: repeating-linear-gradient(
        135deg,
        rgba(244, 67, 54, 0.14) 0 4px,
        transparent 4px 8px
      );
    `}

  ${(p) =>
    p.$missing &&
    css`
      color: ${ThemeColors.gray[600]};
      background-color: ${ThemeColors.gray[100]};
    `}

  &:active {
    box-shadow: inset 0 0 0 2px ${ThemeColors.primary.dark};
  }
`;

const CellName = styled.span`
  display: block;
  word-break: break-word;
`;

const CellMeta = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 0.85em;
  font-weight: ${Typography.fontWeight.regular};
  color: ${ThemeColors.text.hint};
  font-variant-numeric: tabular-nums;
`;

export const RackView: React.FC<ViewProps & { zoom: RackZoom }> = ({
  panel,
  quantities,
  onSelectSlot,
  query,
  filter,
  zoom,
}) => {
  const metrics = RACK_METRICS[zoom];
  const isVisible = useSlotVisibility({ quantities, query, filter });
  const spotlighting = Boolean(query) || filter !== 'all';

  return (
    <RackScroll>
      <RackGrid $cols={panel.colCount} $track={metrics.track} $fit={zoom === 'fit'}>
        <ColHeader aria-hidden="true" />
        {Array.from({ length: panel.colCount }, (_, c) => (
          <ColHeader key={`col-${c}`} aria-hidden="true">
            {c + 1}
          </ColHeader>
        ))}

        {panel.slots.map((row, rowIndex) => (
          <React.Fragment key={`row-${rowIndex}`}>
            <Gutter aria-hidden="true">{rowIndex + 1}</Gutter>
            {row.map((slot) => {
              const qty = quantities[slot.product.key] ?? 0;
              const state = slotState(slot.product);
              return (
                <RackCell
                  key={`${rowIndex}-${slot.col}`}
                  $height={metrics.height}
                  $font={metrics.font}
                  $accent={FORM_COLORS[slot.product.form]}
                  $dimmed={spotlighting && !isVisible(slot.product)}
                  $oos={state === 'oos'}
                  $missing={state === 'missing'}
                  $ordered={qty > 0}
                  onClick={() => {
                    tapFeedback();
                    onSelectSlot(slot);
                  }}
                  aria-label={`Shelf ${slot.row + 1} facing ${slot.col + 1}: ${
                    slot.product.name
                  }${qty > 0 ? `, ${qty} ordered` : ''}`}
                >
                  {qty > 0 && <QtyBadge>{qty}</QtyBadge>}
                  <CellName>{slot.product.rackName}</CellName>
                  {zoom !== 'fit' && (
                    <CellMeta>
                      {slot.product.itemCode ?? slot.product.rackCode}
                      {slot.product.price !== null && ` · ${formatPrice(slot.product.price)}`}
                    </CellMeta>
                  )}
                </RackCell>
              );
            })}
          </React.Fragment>
        ))}
      </RackGrid>
    </RackScroll>
  );
};

/* ── Shelf view ────────────────────────────────────────────────────────────── */

const ShelfBlock = styled.section`
  margin-bottom: ${Spacing.md};
`;

const ShelfHeader = styled.header`
  display: flex;
  align-items: baseline;
  gap: ${Spacing.sm};
  padding: 0 2px ${Spacing.xs};
`;

const ShelfTitle = styled.h3`
  margin: 0;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.primary.dark};
`;

const ShelfHint = styled.span`
  font-size: 11px;
  color: ${ThemeColors.text.hint};
`;

const ShelfStrip = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding: 2px 2px ${Spacing.sm};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ShelfCard = styled.div<{ $accent: string; $ordered: boolean }>`
  flex: 0 0 46%;
  min-width: 150px;
  scroll-snap-align: start;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${Spacing.sm};
  border: 1px solid ${(p) => (p.$ordered ? ThemeColors.primary.main : ThemeColors.border.light)};
  border-left: 4px solid ${(p) => p.$accent};
  border-radius: ${BorderRadius.md};
  background: ${(p) => (p.$ordered ? '#F1F8ED' : ThemeColors.background.paper)};
  box-shadow: ${Shadows.xs};
`;

/**
 * The tappable region of a card. Kept separate from the card itself so the
 * quantity stepper's buttons are not nested inside another button.
 */
const CardTap = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  font-family: ${Typography.fontFamily.primary};
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
`;

const FacingTag = styled.span`
  font-size: 10px;
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.hint};
  letter-spacing: 0.04em;
`;

const CardName = styled.strong`
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  line-height: 1.25;
`;

const CardRackName = styled.span`
  font-size: 10px;
  color: ${ThemeColors.text.hint};
  line-height: 1.2;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.xs};
  margin-top: auto;
  padding-top: ${Spacing.xs};
`;

const CardPrice = styled.span`
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.primary.dark};
  font-variant-numeric: tabular-nums;
`;

export const ShelfView: React.FC<ViewProps> = ({
  panel,
  quantities,
  onSelectSlot,
  onChangeQuantity,
  query,
  filter,
}) => {
  const isVisible = useSlotVisibility({ quantities, query, filter });
  const shelves = panel.slots
    .map((row, rowIndex) => ({ rowIndex, slots: row.filter((s) => isVisible(s.product)) }))
    .filter((shelf) => shelf.slots.length > 0);

  if (shelves.length === 0) {
    return <EmptyState>No facings on this panel match your search.</EmptyState>;
  }

  return (
    <>
      {shelves.map(({ rowIndex, slots }) => (
        <ShelfBlock key={rowIndex}>
          <ShelfHeader>
            <ShelfTitle>Shelf {rowIndex + 1}</ShelfTitle>
            <ShelfHint>
              {slots.length} of {panel.colCount} facings
            </ShelfHint>
          </ShelfHeader>
          <ShelfStrip>
            {slots.map((slot) => {
              const qty = quantities[slot.product.key] ?? 0;
              return (
                <ShelfCard
                  key={slot.col}
                  $accent={FORM_COLORS[slot.product.form]}
                  $ordered={qty > 0}
                >
                  <CardTap
                    onClick={() => {
                      tapFeedback();
                      onSelectSlot(slot);
                    }}
                    aria-label={`${slot.product.name}, shelf ${rowIndex + 1} facing ${
                      slot.col + 1
                    }`}
                  >
                    <FacingTag>{positionBadge(slot)}</FacingTag>
                    <CardName>{slot.product.name}</CardName>
                    <CardRackName>{slot.product.rackName}</CardRackName>
                    <ProductStatus product={slot.product} />
                  </CardTap>
                  <CardFooter>
                    <CardPrice>{formatPrice(slot.product.price)}</CardPrice>
                    <QtyStepper
                      qty={qty}
                      size={32}
                      label={slot.product.name}
                      onChange={(next) => onChangeQuantity(slot.product, next)}
                    />
                  </CardFooter>
                </ShelfCard>
              );
            })}
          </ShelfStrip>
        </ShelfBlock>
      ))}
    </>
  );
};

/* ── List view ─────────────────────────────────────────────────────────────── */

const ListShelfHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.xs} ${Spacing.sm};
  margin: ${Spacing.sm} 0 ${Spacing.xs};
  border-radius: ${BorderRadius.sm};
  background: ${ThemeColors.primary.dark};
  color: #fff;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
`;

const ListRow = styled.div<{ $accent: string; $ordered: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  width: 100%;
  min-height: 60px;
  padding: ${Spacing.sm};
  margin-bottom: ${Spacing.xs};
  border: 1px solid ${(p) => (p.$ordered ? ThemeColors.primary.main : ThemeColors.border.light)};
  border-left: 4px solid ${(p) => p.$accent};
  border-radius: ${BorderRadius.md};
  background: ${(p) => (p.$ordered ? '#F1F8ED' : ThemeColors.background.paper)};
`;

/** Tappable region of a list row; siblings of the stepper, never its ancestor. */
const RowTap = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: 0;
  border: none;
  background: transparent;
  font-family: ${Typography.fontFamily.primary};
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
`;

const FacingPill = styled.span`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: ${BorderRadius.round};
  background: ${ThemeColors.background.default};
  color: ${ThemeColors.primary.dark};
  font-size: 11px;
  font-weight: ${Typography.fontWeight.bold};
  font-variant-numeric: tabular-nums;
`;

const ListBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ListName = styled.strong`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  line-height: 1.25;
`;

const ListMeta = styled.span`
  font-size: 11px;
  line-height: 1.3;
  color: ${ThemeColors.text.hint};
  font-variant-numeric: tabular-nums;
`;

export const ListView: React.FC<ViewProps> = ({
  panel,
  quantities,
  onSelectSlot,
  onChangeQuantity,
  query,
  filter,
}) => {
  const isVisible = useSlotVisibility({ quantities, query, filter });
  const shelves = panel.slots
    .map((row, rowIndex) => ({ rowIndex, slots: row.filter((s) => isVisible(s.product)) }))
    .filter((shelf) => shelf.slots.length > 0);

  if (shelves.length === 0) {
    return <EmptyState>No facings on this panel match your search.</EmptyState>;
  }

  return (
    <>
      {shelves.map(({ rowIndex, slots }) => (
        <section key={rowIndex}>
          <ListShelfHeader>
            <span>Shelf {rowIndex + 1}</span>
            <span>
              {slots.length}/{panel.colCount}
            </span>
          </ListShelfHeader>
          {slots.map((slot) => {
            const qty = quantities[slot.product.key] ?? 0;
            return (
              <ListRow
                key={slot.col}
                $accent={FORM_COLORS[slot.product.form]}
                $ordered={qty > 0}
              >
                <RowTap
                  onClick={() => {
                    tapFeedback();
                    onSelectSlot(slot);
                  }}
                  aria-label={`${slot.product.name}, shelf ${rowIndex + 1} facing ${slot.col + 1}`}
                >
                  <FacingPill aria-hidden="true">F{slot.col + 1}</FacingPill>
                  <ListBody>
                    <ListName>{slot.product.name}</ListName>
                    <ListMeta>
                      {[
                        slot.product.rackName,
                        slot.product.itemCode,
                        slot.product.price !== null ? formatPrice(slot.product.price) : null,
                      ]
                        .filter(Boolean)
                        .join(' \u00B7 ')}
                    </ListMeta>
                    <ProductStatus product={slot.product} />
                  </ListBody>
                </RowTap>
                <QtyStepper
                  qty={qty}
                  size={38}
                  label={slot.product.name}
                  onChange={(next) => onChangeQuantity(slot.product, next)}
                />
              </ListRow>
            );
          })}
        </section>
      ))}
    </>
  );
};
